import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Users, AlertTriangle, PauseCircle, ChevronRight, TrendingUp } from 'lucide-react';
import { serviceCallsApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/**
 * The service team, at a glance: who is working, who is free, how many calls
 * are waiting for someone — and, per person, how the day went.
 *
 * The number that matters most is "Idle while calls waited": minutes someone
 * was checked in, had nothing in progress, and a call sat unallocated. A call
 * on hold does not count as work in hand, and held time is left out of the
 * completion and pending figures. Only users whose Resource Type is Service or
 * Both appear here.
 */

type Board = NonNullable<Awaited<ReturnType<typeof serviceCallsApi.getTeamBoard>>['data']>;

export const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

/** 95 → "1h 35m", 0 → "—" */
export const dur = (m: number | null | undefined) => {
  if (m === null || m === undefined) return '—';
  if (m <= 0) return '—';
  const h = Math.floor(m / 60), r = m % 60;
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h ? `${h}h ${r}m` : `${r}m`;
};

export const STATE_STYLE: Record<string, string> = {
  busy: 'bg-amber-50 text-amber-700 border-amber-200',
  free: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  off: 'bg-gray-100 text-gray-500 border-gray-200',
};

const ServiceTeam: React.FC = () => {
  const { showError } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // The date lives in the address, so coming back from a person's day keeps it.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') || '') ? params.get('date')! : todayIST();
  const setDate = (d: string) => setParams(d === todayIST() ? {} : { date: d }, { replace: true });
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceCallsApi.getTeamBoard(date);
      setBoard(res.data);
    } catch (e: any) {
      showError('Could not load the team board', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const openDay = (userId: string) => navigate(`/service/team/day?user=${encodeURIComponent(userId)}&date=${date}`);

  // Today is live: refresh every minute so the board says what is true now.
  useEffect(() => {
    if (!board?.is_today) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [board?.is_today, load]);

  const rows = useMemo(() => {
    const order: Record<string, number> = { free: 0, busy: 1, off: 2 };
    return [...(board?.users || [])].sort((a, b) =>
      (order[a.state] - order[b.state]) || (b.idle_while_waiting_minutes - a.idle_while_waiting_minutes) || a.name.localeCompare(b.name));
  }, [board]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    completed: t.completed + r.completed,
    on_hold: t.on_hold + r.on_hold,
    under: t.under + r.pending_under_24h,
    over: t.over + r.pending_over_24h,
    idle: t.idle + r.idle_while_waiting_minutes,
  }), { completed: 0, on_hold: 0, under: 0, over: 0, idle: 0 }), [rows]);

  // Free people while calls are waiting, right now — the situation to act on.
  const waitingWithFree = !!board?.is_today && (board?.unallocated_now || 0) > 0 && (board?.counts.free || 0) > 0;

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1300px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800 flex-1">Service Team</h1>
          <button
            onClick={() => navigate('/service/team/performance')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
            title="All staff and each person over a date range"
          >
            <TrendingUp className="h-4 w-4" /> Performance
          </button>
          <input
            type="date"
            value={date}
            max={todayIST()}
            onChange={e => setDate(e.target.value || todayIST())}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded"
          />
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {board && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className={`rounded-lg border p-3 ${board.is_today && board.unallocated_now ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-[11px] font-bold uppercase text-gray-500">Unallocated now</div>
                <div className={`text-2xl font-bold ${board.is_today && board.unallocated_now ? 'text-red-700' : 'text-gray-700'}`}>{board.is_today ? board.unallocated_now : '—'}</div>
                <div className="text-[11px] text-gray-500">{!board.is_today ? 'a live figure — shown for today' : board.oldest_unallocated_minutes !== null ? `oldest waiting ${dur(board.oldest_unallocated_minutes)}` : 'nothing waiting'}</div>
              </div>
              <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Free</div>
                <div className="text-2xl font-bold text-emerald-700">{board.is_today ? board.counts.free : '—'}</div>
                <div className="text-[11px] text-gray-500">checked in, nothing in progress</div>
              </div>
              <div className="rounded-lg border p-3 bg-amber-50 border-amber-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Busy</div>
                <div className="text-2xl font-bold text-amber-700">{board.is_today ? board.counts.busy : '—'}</div>
                <div className="text-[11px] text-gray-500">has a call in progress</div>
              </div>
              <div className="rounded-lg border p-3 bg-gray-50 border-gray-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Off</div>
                <div className="text-2xl font-bold text-gray-600">{board.is_today ? board.counts.off : '—'}</div>
                <div className="text-[11px] text-gray-500">not checked in</div>
              </div>
            </div>

            {waitingWithFree && (
              <div className="flex gap-2 p-3 mb-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{board.unallocated_now}</strong> call{board.unallocated_now === 1 ? ' is' : 's are'} waiting
                  while <strong>{board.counts.free}</strong> {board.counts.free === 1 ? 'person is' : 'people are'} free.
                </span>
              </div>
            )}

            {rows.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                No service users. Set Resource Type to Service or Both in the user master.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">User</th>
                      <th className="px-3 py-2 text-left">Now</th>
                      <th className="px-3 py-2 text-right" title="Calls closed on this day">Completed</th>
                      <th className="px-3 py-2 text-right" title="Taken to closed, held time left out">Avg time</th>
                      <th className="px-3 py-2 text-right" title="The longest one closed on this day">Max time</th>
                      <th className="px-3 py-2 text-right" title="In hand for less than 24 hours, held time left out">Pending &lt;24h</th>
                      <th className="px-3 py-2 text-right" title="In hand for 24 hours or more, held time left out">Pending &gt;24h</th>
                      <th className="px-3 py-2 text-right" title="Calls parked on hold right now">On hold</th>
                      <th className="px-3 py-2 text-right" title="Checked in, nothing in progress, and a call was waiting unallocated">Idle while calls waited</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map(r => {
                      const flag = board.is_today && r.state === 'free' && board.unallocated_now > 0;
                      return (
                        <React.Fragment key={r.user_id}>
                        <tr onClick={() => openDay(r.user_id)} className={`cursor-pointer hover:bg-blue-50/50 ${flag ? 'bg-red-50' : ''}`} title="Show this person's day, stretch by stretch">
                          <td className="px-3 py-2 font-semibold text-gray-800">
                            <span className="inline-flex items-center gap-1">
                              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                              {r.name}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {board.is_today ? (
                              <span className={`px-2 py-0.5 rounded border text-xs font-bold uppercase ${STATE_STYLE[r.state]}`}>{r.state}</span>
                            ) : <span className="text-gray-300">—</span>}
                            {board.is_today && r.current_call && (
                              <span className="ml-2 text-[11px] text-gray-500">#{r.current_call.id} · {dur(r.current_call.minutes)}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.completed || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{dur(r.avg_completion_minutes)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{dur(r.max_completion_minutes)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.pending_under_24h || '—'}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${r.pending_over_24h ? 'text-red-600 font-bold' : ''}`}>{r.pending_over_24h || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.on_hold ? <span className="inline-flex items-center gap-1 text-slate-600"><PauseCircle className="h-3.5 w-3.5" />{r.on_hold}</span> : '—'}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${r.idle_while_waiting_minutes >= 30 ? 'text-red-600 font-bold' : ''}`}>
                            {dur(r.idle_while_waiting_minutes)}
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold text-gray-700">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>Team</td>
                      <td className="px-3 py-2 text-right tabular-nums">{totals.completed}</td>
                      <td className="px-3 py-2" colSpan={2}></td>
                      <td className="px-3 py-2 text-right tabular-nums">{totals.under}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{totals.over}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{totals.on_hold}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{dur(totals.idle)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
              Free, busy and off are for right now, so they show only for today. Idle time counts from check-in to
              check-out; a day with no check-out runs to now (today) or nine hours. A call on hold does not count as
              work in hand, and held time is left out of the times and pending figures. Click a person to open their day
              on its own page: every busy and free stretch, and the calls waiting for allocation while they were free.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceTeam;
